import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/news";

const lines = ["LIGER HAS", "GONE FULL", "SURVIVAL MODE"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.25, 1, 0.5, 1], delay: 0.35 + i * 0.15 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scanlines">
        <img src={IMAGES.heroTopdown} alt="Top-down view of Liger fishing at a forest lake" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/55 to-[#0A0A0B]/30" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 px-6 sm:px-10 pb-16 pt-40 max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="flex flex-wrap items-center gap-4 mb-8"
          data-testid="hero-kicker"
        >
          <span className="bg-[#EF4444] text-white font-mono-news text-xs uppercase tracking-widest px-3 py-1.5">
            Breaking · Hiatus Watch
          </span>
          <span className="font-mono-news text-xs uppercase tracking-widest text-zinc-300">
            Exclusive Field Report — June 2026
          </span>
        </motion.div>

        <h1 data-testid="hero-headline" className="font-display text-white leading-[0.9] tracking-tight text-7xl sm:text-8xl lg:text-[9vw]">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                {i === 2 ? (
                  <>
                    SURVIVAL <span className="text-[#4ADE80]">MODE</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subhead"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed"
        >
          The beloved Minecraft YouTuber has vanished into the wilderness — surviving on fish and
          berries, borrowing electricity from strangers' cabins, and refusing to log on until he's
          ready. His friends are waiting. So are we.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-10 flex items-center gap-6"
        >
          <a
            href="#article"
            data-testid="hero-cta-read"
            className="group inline-flex items-center gap-3 bg-[#4ADE80] text-black font-mono-news text-xs uppercase tracking-widest px-6 py-4 hover:bg-white transition-colors duration-300"
          >
            Read The Full Report
            <span className="group-hover:translate-y-0.5 transition-transform duration-300">↓</span>
          </a>
          <span className="font-mono-news text-xs text-zinc-500 uppercase tracking-widest">
            6 min read<span className="blink-cursor text-[#4ADE80]">_</span>
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
